import type { IPendingScheduleBucket } from "store/home/types";
import dollarsToCents from "helpers/dollarsToCents";
import saveCsvFile from "helpers/saveCsvFile";
import { escapeCsvCell } from "../../Receivables/uploadResultsCsv";

const PENDING_SCHEDULE_CSV_HEADER =
  "bucket,label,window_start,window_end,amount,invoice_count,share_pct,running_total";

export interface IPendingScheduleRow extends IPendingScheduleBucket {
  amountCents: number;
  sharePct: number;
  runningTotalCents: number;
}

export const centsToFixed = (cents: number) => (cents / 100).toFixed(2);

export const buildPendingScheduleRows = (
  buckets: IPendingScheduleBucket[]
): IPendingScheduleRow[] => {
  const totalCents = buckets.reduce((total, bucket) => total + dollarsToCents(bucket.amount), 0);
  let runningTotalCents = 0;

  return buckets.map((bucket) => {
    const amountCents = dollarsToCents(bucket.amount);
    runningTotalCents += amountCents;
    return {
      ...bucket,
      amountCents,
      sharePct: totalCents ? (amountCents / totalCents) * 100 : 0,
      runningTotalCents
    };
  });
};

export const buildPendingScheduleCsv = (buckets: IPendingScheduleBucket[]) => {
  const lines = buildPendingScheduleRows(buckets).map((row) =>
    [
      String(row.bucket),
      escapeCsvCell(row.label),
      escapeCsvCell(row.window_start ?? ""),
      escapeCsvCell(row.window_end ?? ""),
      centsToFixed(row.amountCents),
      String(row.count),
      row.sharePct.toFixed(2),
      centsToFixed(row.runningTotalCents)
    ].join(",")
  );

  return [PENDING_SCHEDULE_CSV_HEADER, ...lines].join("\n");
};

export const pendingScheduleCsvFilename = (asOf?: string) => {
  const stamp = asOf ? new Date(asOf) : new Date();
  const date = Number.isNaN(stamp.getTime()) ? new Date() : stamp;
  const datePart = [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0")
  ].join("-");
  return `pending_balance_schedule_${datePart}.csv`;
};

export default function downloadPendingScheduleCsv(
  buckets: IPendingScheduleBucket[],
  asOf?: string
) {
  const csv = buildPendingScheduleCsv(buckets);
  return saveCsvFile(`${csv}\n`, pendingScheduleCsvFilename(asOf));
}
